/**
 * IntakeStatusPage — `/partner/intake/status`
 *
 * Phase 1c. Reads `/api/poopartner/intake/me` and renders one of 4
 * states (submitted / under_review / approved / rejected) with
 * masked EIN (last-4 only) and the appropriate CTAs.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import PublicPageShell from "@/components/PublicPageShell";
import { Loader2, Clock, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

const STATUS_META = {
  submitted: {
    icon: Clock,
    title: "Your application is in the queue",
    body: "Thanks — we've received your business verification submission. Our team will review it shortly.",
  },
  under_review: {
    icon: Clock,
    title: "We're reviewing your application",
    body: "A PooPluckers admin is reviewing your documents. This normally takes 1-2 business days.",
  },
  approved: {
    icon: CheckCircle2,
    title: "You're a verified PooPartner",
    body: "Welcome aboard. Head to your dashboard to complete your service-area setup and start accepting bookings.",
  },
  rejected: {
    icon: XCircle,
    title: "Your application was not approved",
    body: "See the note from our review team below. You can address the concern and submit a new application.",
  },
};

export default function IntakeStatusPage() {
  const [loading, setLoading] = useState(true);
  const [intake, setIntake] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/poopartner/intake/me");
        if (!cancelled) setIntake(data);
      } catch (e) {
        if (!cancelled) {
          if (e.response?.status === 404) {
            setError("no_intake");
          } else {
            setError("fetch_failed");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <PublicPageShell mainClass="flex items-center justify-center py-16">
        <Loader2
          className="animate-spin text-pp-text-muted"
          size={28}
          data-testid="intake-status-loader"
        />
      </PublicPageShell>
    );
  }

  if (error === "no_intake") {
    return (
      <PublicPageShell mainClass="flex items-center justify-center py-16 px-4">
        <div
          className="max-w-lg text-center bg-white border border-pp-border rounded-lg p-8"
          data-testid="intake-status-no-intake"
        >
          <h1 className="text-2xl font-semibold text-pp-text mb-2">
            No application on file
          </h1>
          <p className="text-sm text-pp-text-muted mb-4">
            You haven't submitted a PooPartner business verification yet.
          </p>
          <Button asChild data-testid="intake-status-start-btn">
            <Link to="/partner/business-intake">Start your application</Link>
          </Button>
        </div>
      </PublicPageShell>
    );
  }

  if (error === "fetch_failed" || !intake) {
    return (
      <PublicPageShell mainClass="flex items-center justify-center py-16 px-4">
        <p
          className="text-sm text-red-600"
          data-testid="intake-status-fetch-error"
        >
          Couldn't load your application status. Please try again.
        </p>
      </PublicPageShell>
    );
  }

  const meta = STATUS_META[intake.status] || STATUS_META.submitted;
  const Icon = meta.icon;

  return (
    <PublicPageShell mainClass="flex justify-center px-4 py-10">
      <div
        className="w-full max-w-2xl bg-white border border-pp-border rounded-lg p-6 md:p-10"
        data-testid={`intake-status-page-${intake.status}`}
      >
        <div className="flex items-start gap-3">
          <Icon size={28} className="text-pp-orange mt-1" aria-hidden="true" />
          <div className="flex-1">
            <h1
              className="text-2xl font-semibold text-pp-text"
              data-testid="intake-status-title"
            >
              {meta.title}
            </h1>
            <p
              className="text-sm text-pp-text-muted mt-1"
              data-testid="intake-status-body"
            >
              {meta.body}
            </p>
          </div>
        </div>

        <dl className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 text-sm">
          <div>
            <dt className="text-pp-text-muted">Business name</dt>
            <dd
              className="font-medium text-pp-text"
              data-testid="intake-status-business-name"
            >
              {intake.business_name}
            </dd>
          </div>
          <div>
            <dt className="text-pp-text-muted">EIN</dt>
            <dd className="font-mono text-pp-text" data-testid="intake-status-ein-masked">
              XX-XXX{intake.ein_last4}
            </dd>
          </div>
          <div>
            <dt className="text-pp-text-muted">Entity type</dt>
            <dd className="text-pp-text">{intake.legal_entity_type}</dd>
          </div>
          <div>
            <dt className="text-pp-text-muted">Submitted</dt>
            <dd className="text-pp-text">
              {new Date(intake.submitted_at).toLocaleString()}
            </dd>
          </div>
        </dl>

        {intake.status === "rejected" && intake.rejection_reason && (
          <div
            className="mt-6 border border-red-200 bg-red-50 rounded p-3 text-sm text-red-700"
            data-testid="intake-status-rejection-reason"
          >
            <strong>Reviewer note:</strong> {intake.rejection_reason}
          </div>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          {intake.status === "rejected" && (
            <Button asChild data-testid="intake-status-resubmit-btn">
              <Link to="/partner/business-intake">Submit a new application</Link>
            </Button>
          )}
          {intake.status === "approved" && (
            <Button asChild data-testid="intake-status-dashboard-btn">
              <Link to="/dashboard/provider">Go to my dashboard</Link>
            </Button>
          )}
        </div>
      </div>
    </PublicPageShell>
  );
}
